import {AfterViewInit, Component, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {ApiService} from '../../_services/api.service';
import {ActivatedRoute, Params} from '@angular/router';
import {FormElement, FormPage, SpecialMapping, WebFormTemplate} from './models/WebFormTemplate';
import {UntypedFormArray, UntypedFormGroup} from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import {CustomFormControl} from './components/controls/CustomFormControl';
import {slideInOutAnimation} from '../../_animations/animations';
import {WebFormFieldSummary, WebFormSummary} from './models/WebFormSummary';
import {FormHelperService} from './services/form-helper.service';
import {SendDocumentResponse} from './models/api/SendDocumentResponse';
import {ViewportScroller} from '@angular/common';
import {AmpUserInfo, AmpUserInfoPayload} from './models/AmpUserInfo';
import {AttachmentDto} from './models/AttachmentDto';
import {Subscription} from 'rxjs';
import {FormSerializerService} from './services/form-serializer.service';
import {PingDocScanResponse} from './models/api/PingDocScanResponse';
import {FormFAQ} from './models/api/FormFAQ';
import {NgbTooltipConfig} from '@ng-bootstrap/ng-bootstrap';
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import {FormDetail} from './models/api/Form';
import {FormOutlineComponent} from './components/form-outline/form-outline.component';
import {SubmissionEvent} from './models/SubmissionEvent';
import {FileOnlineViewModel} from './FileOnlineViewModel';
import {ModalAlertMessage} from './models/ModalAlertMessage';
import {Title} from '@angular/platform-browser';
import {EnvironmentService} from '../../_services/environment.service';
import {ConfigOption} from '../../_models/ConfigOption';

export enum FormLoadState {
  loading,
  errored, // 404,500
  loaded
}

@Component({
  selector: 'app-file-online-v2',
  templateUrl: './file-online-v2.component.html',
  styleUrls: ['./file-online-v2.component.scss'],
  animations: [slideInOutAnimation]
})
export class FileOnlineV2Component implements OnInit, AfterViewInit, OnDestroy {

  public readonly FormLoadState = FormLoadState;

  // Global view model
  viewModel = new FileOnlineViewModel();

  // Temp data that is specific to forms
  pageIndex = 0;
  formProgress = 0; // % progress
  outlineItemIndex = 0;
  outlineItems: string[] = [];
  formSummaries: WebFormSummary[] = [];
  config = {};
  // When errors are encountered
  eFileDisabled = false; // If AMP offline or maintenance mode
  eFileDisabledToolTip: string | undefined;
  eFileSubmissionError: string | null;
  formLoadState = FormLoadState.loading;
  // Created when the form is loaded
  faq: FormFAQ;
  formDetail: FormDetail | null;
  formId: string;
  formPages: FormPage[] = [];
  templates = new Map<string, FormElement[]>();
  controls = new Map<string, CustomFormControl>();
  specialMappings: SpecialMapping[] = [];
  // Signix page data
  isSignixFrameLoading = false;
  documentResponse: SendDocumentResponse | null;
  // Misc
  devMode = false; // Useful for debugging non-published forms
  submissionCount = 0;
  isSubmitting = false;
  userInfo: AmpUserInfo | null;
  autofillValues = new Map<string, string>();
  useTemplateFromDatabase = true;
  modalRef: BsModalRef;
  pdfPreviewUrl: string | null;
  @ViewChild(FormOutlineComponent) formOutline!: FormOutlineComponent;
  // Listen for modal
  private modalAlertSubscription: Subscription;
  @ViewChild('genericAlert') genericAlertTemplate: TemplateRef<HTMLInputElement>;

  constructor(private apiService: ApiService,
              private route: ActivatedRoute,
              private http: HttpClient,
              public formHelper: FormHelperService,
              private formSerializer: FormSerializerService,
              private viewportScroller: ViewportScroller,
              private ngbToolTipService: NgbTooltipConfig,
              private modalService: BsModalService,
              private titleService: Title,
              private environmentService: EnvironmentService) {}

  ngOnInit(): void {
    this.listenForQueryParamChanges();
    this.listenForModalAlerts();

    // Note: Apparently, NgbToolTip requires the presence of an enclosing
    // tag container with value 'body'. You can add container="body" to
    // individual <input> elements OR you can set it globally in the config.
    this.ngbToolTipService.container = 'body';
  }

  ngAfterViewInit(): void {
    window.addEventListener('resize', this.onWindowResize);
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.onWindowResize);
    this.modalAlertSubscription.unsubscribe();
  }

  private listenForModalAlerts(): void {
    this.modalAlertSubscription = this.viewModel.modalTriggerObservable.subscribe((message: ModalAlertMessage) => {
      this.viewModel.modalAlertMessage = message;
      this.modalRef = this.modalService.show(this.genericAlertTemplate);
    });
  }

  private listenForQueryParamChanges(): void {
    this.route.queryParams.subscribe((params: Params) => {
      if (params.dev) {
        this.devMode = true;
      }

      if (params.local) {
        this.useTemplateFromDatabase = false;
      }

      const formId = params.id;
      if (formId === undefined) { return; }

      this.formId = formId;
      this.loadConfiguration(formId).then();
    });
  }

  // Load all the information needed to properly display the form.
  async loadConfiguration(formId: string): Promise<void> {
    try {
      // The form may be known by another name in the Assessor api
      const assessorApiFormattedFormId = this.getAssessorApiFormattedId(this.formId);

      // Load credentials, settings, templates
      const [[ampUserInfo, ampUserInfoPayloads], siteSettings, formDetail, formFAQ] = await Promise.all([
        this.apiService.getAmpUserInfo(),
        this.apiService.siteSettings().toPromise(),
        this.apiService.getForm(assessorApiFormattedFormId).toPromise(),
        this.apiService.getFormFaq(formId).toPromise()
      ]);

      // Process site specific settings (e.g., maintenance mode)
      this.processSiteSettings(siteSettings);
      // Cache AMP credentials to be autofilled when the form template is loaded
      this.processAmpCredentials(ampUserInfo, ampUserInfoPayloads);

      // Create the UI components from the data stored in the database or in the /assets/forms folder
      if (this.useTemplateFromDatabase) {
        const webFormTemplate = JSON.parse(formFAQ.formTemplate) as WebFormTemplate;
        this.processWebFormTemplate(formFAQ, formDetail, webFormTemplate);
      } else {
        const localWebFormTemplate = await this.http.get<WebFormTemplate>(`assets/forms/${formId.toUpperCase()}.json`).toPromise();
        this.processWebFormTemplate(formFAQ, formDetail, localWebFormTemplate);
      }

      if (formDetail.eFileMaintenance && !this.devMode) {
        this.disableEFile();
      }

      // Load the AMP status
      let ampIsOffline = false;
      const ampStatusResponse = await this.apiService.getAmpStatus().toPromise();
      if (ampStatusResponse instanceof PingDocScanResponse) {
        ampIsOffline = ampStatusResponse.statusCode !== 200;
      } else if (ampStatusResponse.status !== 200) {
        ampIsOffline = true;
      }

      if (ampIsOffline && !this.devMode) {
        this.disableEFile();
      }
    } catch {
      this.formLoadState = FormLoadState.errored;

      if (!this.devMode) {
        this.disableEFile();
      }
    }
  }

  disableEFile(): void {
    this.eFileDisabled = true;
    this.eFileDisabledToolTip = 'The Assessor e-File system is currently down for maintenance. Please try again later.';
  }

  private processSiteSettings(options: ConfigOption[]): void {
    const finalConfig = {};
    options.forEach(option => {
      finalConfig[option.setting] = option.data;
    });
    this.config = finalConfig;
  }

  private processAmpCredentials(ampUserInfo: AmpUserInfo, ampUserInfoFields: AmpUserInfoPayload[]): void {
    if (ampUserInfoFields) {
      ampUserInfoFields.forEach((payload: AmpUserInfoPayload) => {
        this.autofillValues.set(payload.key, payload.value);
      });
    }

    this.userInfo = ampUserInfo;
  }

  private processWebFormTemplate(faq: FormFAQ, formDetail: FormDetail, webFormTemplate: WebFormTemplate): void {
    // Create the form and assign data
    const createdFormInfo = this.formHelper.createForm(webFormTemplate, faq, this.viewModel);
    this.formDetail = formDetail;
    this.faq = faq;
    this.formPages = createdFormInfo.formPages;
    this.templates = createdFormInfo.templates;
    this.controls = createdFormInfo.controls;
    this.specialMappings = createdFormInfo.specialMappings;

    // Update autofill if necessary
    this.fillInValues();
    // Update the outline
    this.updateOutlineItems();
    // Update the page title
    this.titleService.setTitle(`${this.environmentService.getRootTabName()} - ` + faq.formName);

    const isMaintenanceMode = this.config.hasOwnProperty('EFI_Maintenance_Banner');
    this.formLoadState = FormLoadState.loaded;

    // If this form is unpublished, or AMP is offline, or maintenance mode is enabled and devMode is NOT true, disable
    if ((!formDetail.hasEFile || isMaintenanceMode) && !this.devMode) {
      this.disableEFile();
    }
  }

  private getAssessorApiFormattedId(formNumber: string): string {
    switch (formNumber.toLowerCase()) {
      case 'boe-266-assr-515':
        return 'boe-266 / assr-515'; // We need to do this to properly load the FormDetail from the assessor api ;(
      case 'ads-820.2':
        return 'ads-820';
      default:
        return formNumber;
    }
  }

  private fillInValues(): void {
    // Attempt to auto fill
    this.controls.forEach((control: CustomFormControl) => {
      if (!control.element.autofillKey) { return; }
      const autoFillValue = this.autofillValues.get(control.element.autofillKey);
      if (!autoFillValue) { return; }
      control.setValue(autoFillValue, { emitEvent: false });
    });
  }

  // --------- GETTERS ---------

  getFormPage(type: string): FormPage | null {
    if (this.formPages.length === 0) { return null; }
    let page = this.formPages.length - 1;
    while (page > 0) {
      const formPage = this.formPages[page];
      if (formPage.type === type) { return formPage; }
      --page;
    }

    return null;
  }

  isPageVisible(): boolean {
    return this.isSpecificPageVisible(this.pageIndex);
  }

  isSpecificPageVisible(pageIndex: number): boolean {
    const formPage = this.formPages[pageIndex];
    return this.formHelper.isFormPageVisible(formPage, this.controls);
  }

  getKeyWithId(id: string, index: number): string {
    return id.replace('{i}', '' + index);
  }

  pageHasFormHints(): boolean {
    const formPage = this.formPages[this.pageIndex];
    return formPage && formPage.hints && formPage.hints.length > 0;
  }

  buildWebFormSummaries(): WebFormSummary[] {
    const summaries: WebFormSummary[] = [];

    for (let i = 0; i < this.formPages.length; ++i) {
      const formPage = this.formPages[i];
      const formGroup = formPage.formGroup;

      if (!this.isSpecificPageVisible(i)) {
        continue;
      }

      const pageFieldsSummary: WebFormFieldSummary[] = [];

      Object.keys(formGroup.controls).forEach(key => {
        const formGroupControl = formGroup.controls[key];

        if (!(formGroupControl instanceof CustomFormControl)) {
          return;
        }
        switch (formGroupControl.element.type) {
          case 'grid':
            break; // Skip grids, let them be serialized by their individual controls
          case 'ssn':
            pageFieldsSummary.push(new WebFormFieldSummary(formGroupControl.element.label, 'XXX-XX-' + formGroupControl.value.slice(-4)));
            break;
          case 'file':
            let fileCount = 1;
            for (const file of formGroupControl.element.files) {
              pageFieldsSummary.push(new WebFormFieldSummary(`File #${fileCount}`, file.name));
              ++fileCount;
            }
            break;
          case 'template':
            this.extractTemplateFieldSummaries(formGroup, formGroupControl, pageFieldsSummary);
            break;
          case 'radio':
            formGroupControl.element.options.forEach(option => {
              if (option.id !== formGroupControl.value) { return; }
              pageFieldsSummary.push(new WebFormFieldSummary(formGroupControl.element.label, option.label));
            });

            break;
          case 'checkbox':
            if (key.startsWith('_')) { return; }
            const displayedValue = formGroupControl.value === true ? 'Yes' : 'No';
            pageFieldsSummary.push(new WebFormFieldSummary(formGroupControl.element.option, displayedValue));
            break;
          default:
            if (key.startsWith('_')) { return; }
            pageFieldsSummary.push(new WebFormFieldSummary(formGroupControl.element.label, formGroupControl.value));
            break;
        }
      });

      if (pageFieldsSummary.length === 0) {
        continue;
      }

      summaries.push(new WebFormSummary(this.formPages[i].title, i, pageFieldsSummary));
    }

    return summaries;
  }

  extractTemplateFieldSummaries(formGroup: UntypedFormGroup,
                                customControl: CustomFormControl,
                                pageFieldsSummary: WebFormFieldSummary[]): void {
    const formArray = formGroup.get(this.formHelper.getFormArrayName(customControl.element.id)) as UntypedFormArray;
    if (!formArray) {
      return;
    }

    let i = 0;
    for (const arrayControl of formArray.controls) {
      if (arrayControl instanceof UntypedFormGroup) {
        Object.keys(arrayControl.controls).forEach(key => {
          const arrayGroupControl = arrayControl.controls[key] as CustomFormControl;
          const keyWithId = this.getKeyWithId(arrayGroupControl.element.label, i + 1);
          pageFieldsSummary.push(new WebFormFieldSummary(keyWithId, arrayGroupControl.value));
        });
      }
      ++i;
    }
  }

  // --------- PAGE NAVIGATION ---------

  smoothScrollToTop(): void {
    this.viewportScroller.scrollToAnchor('formTop');
  }

  doesNotHavePrevious(): boolean {
    return this.pageIndex - 1 < 0;
  }

  isOnLastPage(): boolean {
    return this.pageIndex + 1 === this.formPages.length;
  }

  canProgressOrSubmit(): boolean {
    if (this.isOnLastPage()) { return; }

    const currentForm = this.formPages[this.pageIndex].formGroup;
    return currentForm.valid && !currentForm.pending && this.hasRequiredFiles();
  }

  hasRequiredFiles(): boolean {
    const formPage = this.formPages[this.pageIndex];

    for (const element of formPage.elements) {
      if (element.type !== 'file') { continue; }
      if (element.fileUploadOptional) { return true; }
      if (element.files.length === 0) {
        if (element.fileErrors.length === 0) {
          element.fileErrors = ['You must upload at least one file.'];
        }

        return false;
      }
    }

    return true;
  }

  navigateToPage(type: string): void {
    for (let i = 0; i < this.formPages.length; ++i) {
      if (this.formPages[i].type === type) {
        this.pageIndex = i;
      }
    }

    this.smoothScrollToTop();
    this.updateOutlineItems();
  }

  updateNavigationState(): void {
    this.formPages[this.pageIndex].hasReturnBack = false;
    this.smoothScrollToTop();
    // Rebuild the outline as views may have conditionally been enabled
    this.updateOutlineItems();
  }

  updateOutlineItems(): void {
    const progress = this.pageIndex / (this.formPages.length - 1) * 100.0;
    this.formProgress = Math.ceil(progress);

    const outlineItems: string[] = ['Welcome'];
    let isOnSignaturePage = false;
    let outlinePageIndex = 0;
    let selectedOutlinePageIndex = 0;

    for (let i = 0; i < this.formPages.length; ++i) {
      // Skip the landing page
      if (i === 0) { continue; }
      const formPage = this.formPages[i];
      // Skip pages that are not visible
      if (!this.formHelper.isFormPageVisible(formPage, this.controls)) { continue; }

      outlineItems.push(formPage.title);
      ++outlinePageIndex;

      if (i === this.pageIndex) {
        selectedOutlinePageIndex = outlinePageIndex;
      }

      // Do not include pages after the form confirmation
      if (formPage.type === 'form_preview') {
        outlineItems.push('Signature');
        isOnSignaturePage = this.pageIndex > i;
        break;
      }
    }

    if (isOnSignaturePage) {
      selectedOutlinePageIndex = this.outlineItems.length - 1;
    }

    this.outlineItems = outlineItems;
    this.outlineItemIndex = selectedOutlinePageIndex;

    setTimeout(() => {
      // Set up the initial highlight
      this.formOutline?.updateHighlightPosition();
    }, 50);
  }

  clearFileUploadErrors(): void {
    for (const element of this.formPages[this.pageIndex].elements) {
      element.fileErrors = [];
    }
  }

  // --------- EMITTERS, EVENTS AND BUTTONS ---------
  handlePreviousClicked(): void {
    while (this.pageIndex > 0) {
      this.pageIndex--;
      if (this.isPageVisible()) { break; }
    }

    this.clearFileUploadErrors();
    this.updateNavigationState();
  }

  handleConfirmSubmissionClicked(template: TemplateRef<any>): void {
    this.modalRef = this.modalService.show(template);
  }

  handleSubmissionConfirmedClicked(): void {
    if (this.isSubmitting) {
      return; // Prevent double submissions
    }

    this.modalRef.hide();
    this.handleNextClicked();
  }

  handleNextClicked(): void {
    const formGroup = this.formPages[this.pageIndex].formGroup;
    if (!this.canProgressOrSubmit()) {
      this.formHelper.markFormAsTouched(formGroup);
      return;
    }

    while (this.pageIndex < this.formPages.length) {
      this.pageIndex++;
      if (this.isPageVisible()) { break; }
    }

    this.clearFileUploadErrors();
    this.updateNavigationState();

    switch (this.formPages[this.pageIndex].type) {
      case 'form_summary':
        // Only build the summary when we navigate to it
        this.formSummaries = this.buildWebFormSummaries();
        break;
      case 'form_preview':
        this.generatePdfPreview();
        break;
      case 'form_loading':
        this.submitForm();
        break;
    }
  }

  handleModifyClicked(pageIndex: number): void {
    this.pageIndex = pageIndex;
    this.formPages[pageIndex].hasReturnBack = true;
    this.smoothScrollToTop();
    this.updateOutlineItems();
  }

  handleDownloadFormClicked(): void {
    window.open(this.faq.url, '_blank');
  }

  handleReturnBack(): void {
    if (!this.canProgressOrSubmit()) {
      return;
    }

    this.formPages[this.pageIndex].hasReturnBack = false;
    this.formSummaries = this.buildWebFormSummaries();
    this.navigateToPage('form_summary');
    this.updateOutlineItems();
  }

  handleResubmitClicked(): void {
    this.navigateToPage('form_loading');
    this.submitForm();
  }

  handleConfirmationClicked(): void {
    if (!this.canProgressOrSubmit()) {
      return;
    }

    this.navigateToPage('form_summary');
    this.updateOutlineItems();
  }

  handlePreviewClicked(template: TemplateRef<any>): void {
    this.modalRef = this.modalService.show(template);
    this.generatePdfPreview();
  }

  handleEFileInstructionsClicked(): void {
    if (this.formDetail.instructionUrl) {
      this.formHelper.openLinkInNewTab(this.formDetail.instructionUrl);
    }
  }

  handleReturnToFormsClicked(template: TemplateRef<any>): void {
    this.modalRef = this.modalService.show(template);
  }

  handleSignixTimeoutLinkClicked(): void {
    this.apiService.accessLink(this.documentResponse.onlineFormId).subscribe(signixLink => {
      if (this.formHelper.isSafari()) {
        this.formHelper.openLinkInTab(signixLink.link);
      } else {
        this.formHelper.openLinkInNewTab(signixLink.link);
      }
    });
  }

  // --------- FORM SUBMISSION ---------
  generatePdfPreview(): void {
    // if (this.isDemoMode) {
    //   this.pdfPreviewUrl = 'https://res.cloudinary.com/los-angeles-county-assessor/image/upload/v1726516641/Test/Sample.pdf';
    //   return;
    // }

    this.pdfPreviewUrl = null;

    const json = this.transformFormToJson();
    this.apiService.getRenderedPdf(json).subscribe((res: Blob) => {
      const blob = new Blob([res], {type: 'application/pdf'});
      this.pdfPreviewUrl = URL.createObjectURL(blob);
    });
  }

  transformFormToJson(): string {
    return this.formSerializer.transformFormToJson(
      this.formId,
      this.faq.url,
      this.controls,
      this.formPages,
      this.specialMappings
    );
  }
  submitForm(): void {
    if (this.isSubmitting) {
      console.error('Form is already submitting');
      return;
    }

    this.submissionCount += 1;
    this.isSubmitting = true;

    console.log('Starting form submission');

    const formBody = this.transformFormToJson();
    const files: AttachmentDto[] = [];

    for (const formPage of this.formPages) {
      for (const formElement of formPage.elements) {
        if (!formElement.files) { continue; }

        for (const file of formElement.files) {
          files.push({ isPii: formElement.isPii, file });
        }
      }
    }

    this.apiService.submitForm(formBody, files).subscribe((res: SendDocumentResponse) => {
      this.documentResponse = res;
      if (this.formHelper.isSafari()) {
        this.formHelper.openLinkInTab(res.url);
      } else if (this.formHelper.isMobileDevice()) {
        this.formHelper.openLinkInNewTab(res.url);
      } else {
        const signixPage = this.getFormPage('signix_signature');
        if (!signixPage) { return; }
        this.isSignixFrameLoading = true;
        this.createSignaturePageTimeout();
        this.navigateToPage('signix_signature');
        this.updateOutlineItems();
      }
    }, error => {
      this.handleFormSubmissionError(error);
      this.logSubmissionFailure(null, 'Failed to submit form', error);
    });
  }

  createSignaturePageTimeout(): void {
    setTimeout(() => {
      if (this.isSignixFrameLoading) {
        this.isSignixFrameLoading = false;
      }
    }, 10000);
  }

  logSubmissionFailure(documentGUID: string, friendlyError: string, error: any): void {
    this.isSubmitting = false;

    const submissionEvent = new SubmissionEvent();
    submissionEvent.submitterEmail = this.userInfo?.email;
    submissionEvent.documentGuid = documentGUID;
    submissionEvent.friendlyError = friendlyError;
    submissionEvent.error = JSON.stringify(error);
    submissionEvent.userAgent = navigator.userAgent;
    submissionEvent.submissionCount = this.submissionCount;
    submissionEvent.webFormSubmission = this.transformFormToJson();

    this.apiService.notifySubmissionError(submissionEvent).subscribe();
  }

  handleFormSubmissionError(error: any): void {
    this.navigateToPage('form_error');

    const friendlyError = error.error?.message ?? error.error;
    console.error(error.error);

    if (!friendlyError || typeof friendlyError === 'object') {
      this.eFileSubmissionError = 'The connection was reset. Please resubmit.';
      return;
    }

    // Check for invalid emails
    const invalidEmailPattern = /Value &apos;(.*?)&apos;/;
    const invalidEmailMatch = friendlyError.match(invalidEmailPattern);
    if (invalidEmailMatch) {
      this.eFileSubmissionError = `The provided email ${invalidEmailMatch[1]} is not a valid email.`;
      return;
    }

    // Check for invalid values
    const invalidParam = /parameter &apos;(.*?)&apos;/;
    const invalidParamMatch = friendlyError.match(invalidParam);
    if (invalidParamMatch) {
      const control = this.controls.get(invalidParamMatch[1]);
      this.eFileSubmissionError = `The input ${control?.value ?? ''} for ${invalidParamMatch[1]} is not valid. If this is a name, please remove any special characters.`;
      return;
    }

    this.eFileSubmissionError = friendlyError;
  }

  // --------- JS EVENTS ---------

  onWindowResize = (): void => {
    this.formOutline?.updateHighlightPosition();
  }

  onSignixFrameLoad(): void {
    setTimeout(() => {
      this.isSignixFrameLoading = false;
    }, 1000);
  }

  // --------- COLLECTION OPTIMIZATION HELPER ---------

  trackIndex(index: number): number {
    return index;
  }

  trackFormPage(index: number, formPage: FormPage): string {
    return `${formPage.title}_${index}`;
  }

  // --------- UNUSED ---------

  // private loadDemoMode(): void {
  //   this.formId = 'sample';
  //
  //   this.userInfo = new AmpUserInfo(
  //     'example@assessor.lacounty.gov',
  //     'Smith',
  //     'W',
  //     'John',
  //     '555-100-2000',
  //   );
  //
  //   const userInfoFields: AmpUserInfoPayload[] = [
  //     new AmpUserInfoPayload('AMPMOBILENO', this.userInfo.mobileNo),
  //     new AmpUserInfoPayload('AMPUSERID', this.userInfo.email),
  //     new AmpUserInfoPayload('AMPLASTNAME', this.userInfo.lastName),
  //     new AmpUserInfoPayload('AMPMIDDLENAME', this.userInfo.middleName),
  //     new AmpUserInfoPayload('AMPFIRSTNAME', this.userInfo.firstName),
  //   ];
  //
  //   this.processAmpCredentials(this.userInfo, userInfoFields);
  //
  //   const formFAQ = new FormFAQ();
  //   formFAQ.formName = 'Assessor Form Demo';
  //   formFAQ.faq = 'This is a demo form. The official FAQ will be loaded from the database';
  //   formFAQ.url = 'https://example.com/form.pdf';
  //   formFAQ.pii = true;
  //   formFAQ.optionalPii = false;
  //   formFAQ.multiSigner = false;
  //   formFAQ.rp = true;
  //   formFAQ.pp = false;
  //   formFAQ.pinValidation = false;
  //   formFAQ.formTemplate = 'n/a';
  //
  //   const formDetail = new FormDetail();
  //   formDetail.fileId = 1;
  //   formDetail.fileUrl = 'https://example.com/form.pdf';
  //   formDetail.formNumber = 'assr-demo';
  //   formDetail.title = 'Assessor Demo Form';
  //   formDetail.description = 'This is a demo form. The official FAQ will be loaded from the database';
  //   formDetail.active = true;
  //   formDetail.hasEFile = true;
  //   formDetail.instructionUrl = 'https://example.com/form.pdf';
  //
  //   this.faq = formFAQ;
  //   this.formDetail = formDetail;
  //   this.loadLocalForm('SAMPLE', formFAQ, formDetail);
  // }
}
